import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { ShipmentApiClient } from 'app/core/api-clients/shipment.client';
import { InProgressTreatment, ToothNote, TreatmentShippingAddress } from 'app/core/models';

export interface ShippingLabelData {
  order: InProgressTreatment;
  shippingAddress: TreatmentShippingAddress;
  teethNotes: ToothNote[];
}

@Injectable({
  providedIn: 'root',
})
export class ShippingLabelPdfService {
  private readonly _shipmentClient = inject(ShipmentApiClient);
  private readonly _translate = inject(TranslateService);

  /** Generates and prints the Lab Slip for the supplied order details. */
  async generatePdf(data: ShippingLabelData): Promise<void> {
    return this.generatePdfForTreatment(data.order.id);
  }

  /** Generates and prints the same Lab Slip directly from a treatment list. */
  async generatePdfForTreatment(treatmentId: string): Promise<void> {
    const printWindow: Window | null = window.open('', '_blank');
    let pdfUrl: string;

    try {
      if (printWindow !== null) {
        this.showLoadingPage(printWindow);
      }
      const blob = await firstValueFrom(this._shipmentClient.getLabSlipPdf(treatmentId));
      pdfUrl = URL.createObjectURL(blob);
    } catch (error) {
      printWindow?.close();
      throw error;
    }

    if (printWindow) {
      printWindow.location.href = pdfUrl;
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 1000);

      setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
      return;
    }

    window.open(pdfUrl, '_blank');
    setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
  }

  private showLoadingPage(printWindow: Window): void {
    const document: Document = printWindow.document;
    document.title = this._translate.instant('general.generatingLabSlipTitle');
    document.documentElement.lang = this._translate.currentLang || 'en';

    const viewport: HTMLMetaElement = document.createElement('meta');
    viewport.name = 'viewport';
    viewport.content = 'width=device-width, initial-scale=1';
    const style: HTMLStyleElement = document.createElement('style');
    style.textContent = `
      body { margin: 0; min-height: 100vh; display: grid; place-items: center;
        background: #f5f7fb; color: #273244; font-family: system-ui, sans-serif; }
      main { padding: 40px 24px; max-width: 440px; text-align: center; }
      h1 { margin: 24px 0 12px; font-size: 24px; }
      p { margin: 0; line-height: 1.6; color: #526078; }
      .spinner { display: inline-block; width: 40px; height: 40px; border: 4px solid #e2e6f0;
        border-top-color: #8175f5; border-radius: 50%; animation: spin 1s linear infinite; }
      @keyframes spin { to { transform: rotate(360deg); } }
      @media (prefers-reduced-motion: reduce) { .spinner { animation: none; } }
    `;
    document.head.append(viewport, style);

    const content: HTMLElement = document.createElement('main');
    content.setAttribute('role', 'status');
    content.setAttribute('aria-live', 'polite');
    const spinner: HTMLSpanElement = document.createElement('span');
    spinner.className = 'spinner';
    spinner.setAttribute('aria-hidden', 'true');
    const heading: HTMLHeadingElement = document.createElement('h1');
    heading.textContent = this._translate.instant('general.generatingLabSlipTitle');
    const message: HTMLParagraphElement = document.createElement('p');
    message.textContent = this._translate.instant('general.generatingLabSlipMessage');
    content.append(spinner, heading, message);
    document.body.replaceChildren(content);
  }
}
